import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { createAgency } from '../services/api';
import { useAgency } from '../context/AgencyContext';
import { BuildingOffice2Icon } from '@heroicons/react/24/outline';

export default function AgencyOnboardingPage() {
  const navigate = useNavigate();
  const { refreshAgencies } = useAgency();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      await createAgency({
        name: formData.name,
        slug: formData.slug || undefined,
      });
      await refreshAgencies();
      navigate('/');
    } catch (err: any) {
      console.error('Error creating agency:', err);
      setError(err.message || 'Failed to create agency');
    } finally {
      setLoading(false);
    }
  };

  const generateSlug = (name: string) => {
    return name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="flex justify-center">
          <div className="h-16 w-16 rounded-full bg-primary-100 flex items-center justify-center">
            <BuildingOffice2Icon className="h-8 w-8 text-primary-600" />
          </div>
        </div>
        <h2 className="mt-6 text-center text-3xl font-bold tracking-tight text-gray-900">
          Create Your Agency
        </h2>
        <p className="mt-2 text-center text-sm text-gray-600">
          Set up your AI receptionist agency to start managing client businesses
        </p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
          <form onSubmit={handleSubmit} className="space-y-6">
            {error && (
              <div className="rounded-md bg-red-50 p-4">
                <p className="text-sm text-red-700">{error}</p>
              </div>
            )}

            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                Agency Name *
              </label>
              <div className="mt-1">
                <input
                  id="name"
                  type="text"
                  required
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                  placeholder="My AI Agency"
                  value={formData.name}
                  onChange={(e) => {
                    setFormData({
                      ...formData,
                      name: e.target.value,
                      slug: formData.slug || generateSlug(e.target.value),
                    });
                  }}
                />
              </div>
              <p className="mt-1 text-xs text-gray-500">
                This is your agency's display name
              </p>
            </div>

            <div>
              <label htmlFor="slug" className="block text-sm font-medium text-gray-700">
                Agency Slug
              </label>
              <div className="mt-1">
                <input
                  id="slug"
                  type="text"
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                  placeholder="my-ai-agency"
                  value={formData.slug}
                  onChange={(e) =>
                    setFormData({ ...formData, slug: generateSlug(e.target.value) })
                  }
                />
              </div>
              <p className="mt-1 text-xs text-gray-500">
                Used for URLs and identification (auto-generated from name)
              </p>
            </div>

            <div>
              <button
                type="submit"
                disabled={loading || !formData.name}
                className="flex w-full justify-center rounded-md border border-transparent bg-primary-600 py-2 px-4 text-sm font-medium text-white shadow-sm hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Creating...' : 'Create Agency'}
              </button>
            </div>
          </form>

          <div className="mt-6">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300" />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="bg-white px-2 text-gray-500">What happens next?</span>
              </div>
            </div>

            <div className="mt-6 space-y-4 text-sm text-gray-600">
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <span className="flex h-6 w-6 items-center justify-center rounded-full bg-primary-100 text-primary-600 text-xs font-medium">
                    1
                  </span>
                </div>
                <p className="ml-3">Create your agency account</p>
              </div>
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <span className="flex h-6 w-6 items-center justify-center rounded-full bg-gray-100 text-gray-600 text-xs font-medium">
                    2
                  </span>
                </div>
                <p className="ml-3">Add your first client business</p>
              </div>
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <span className="flex h-6 w-6 items-center justify-center rounded-full bg-gray-100 text-gray-600 text-xs font-medium">
                    3
                  </span>
                </div>
                <p className="ml-3">Configure their AI receptionist</p>
              </div>
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <span className="flex h-6 w-6 items-center justify-center rounded-full bg-gray-100 text-gray-600 text-xs font-medium">
                    4
                  </span>
                </div>
                <p className="ml-3">Start receiving calls!</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
